import React, { useState } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { syncManager } from '../../services/sync';

const CommunicationComposer = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSendMessage = async () => {
    if (!message.trim()) {return;}

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      // Get customer ID from the user's email
      const customers = await syncManager.fetchTabData('Customers', user.email);
      const customer = customers.find(c => c.Email === user.email || c.email === user.email);

      if (!customer) {
        throw new Error('Customer record not found');
      }

      // Add communication to Communications tab
      await syncManager.logAction('Communications', 'create', {
        Timestamp: new Date().toISOString(),
        Client_Name: `${customer.First_Name} ${customer.Last_Name || ''}`.trim(),
        UserID: customer.UserID || '',
        Client_Email: customer.Email,
        Message: message,
        Source: 'Portal',
        Direction: 'Outgoing'
      });

      setSuccess(true);
      setMessage('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {return null;}

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-xl transform transition-all duration-300">
        <div className="flex justify-between items-start p-6 border-b border-slate-100">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <MessageCircle size={20} className="text-blue-600" />
            Send Message
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-slate-100 transition-colors"
          >
            <X size={20} className="text-slate-500 hover:text-slate-700" />
          </button>
        </div>

        <form className="p-6 space-y-4" onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              Error: {error}
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              Message sent successfully!
            </div>
          )}

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            className="w-full min-h-[120px] resize-none border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-700"
            disabled={loading}
          />

          <div className="flex items-center justify-between gap-4">
            <button
              type="submit"
              disabled={loading || !message.trim()}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                'Sending...'
              ) : (
                <>
                  <Send size={18} className="mr-1" />
                  Send Message
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => setMessage('')}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Clear
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommunicationComposer;